/**
 * Conditional Field Service
 * 
 * Handles field visibility logic based on schema-defined dependencies.
 * This service evaluates dependsOn conditions to determine which fields
 * should be visible based on current form data.
 * 
 * Evidence: Backend has 5 TTD-specific fields with dependsOn: { disabilityType: 'TTD' }
 * Located in ClaimController.js lines 478-486
 */

export const conditionalFieldService = {
  /**
   * Evaluate if a field should be visible based on dependsOn conditions
   * @param {Object} fieldDef - Field definition from schema
   * @param {Object} formData - Current form data
   * @returns {boolean} - True if field should be visible
   */
  evaluateFieldVisibility(fieldDef, formData) {
    if (!fieldDef.dependsOn) return true;
    
    return Object.entries(fieldDef.dependsOn).every(([dependentField, expectedValue]) => {
      return formData[dependentField] === expectedValue;
    });
  },

  /**
   * Get all visible fields for a specific claim type
   * @param {Object} schema - Schema object with baseFields and typeSpecificFields
   * @param {string} claimType - Type of claim (DEATH, DISABILITY, CRITICAL_ILLNESS)
   * @param {Object} formData - Current form data
   * @returns {Array} - Array of visible field names
   */
  getVisibleFields(schema, claimType, formData) {
    const baseFields = Object.keys(schema.baseFields || {});
    const typeFields = Object.keys(schema.typeSpecificFields?.[claimType] || {});
    
    const visibleTypeFields = typeFields.filter(fieldName => {
      const fieldDef = schema.typeSpecificFields[claimType][fieldName];
      return this.evaluateFieldVisibility(fieldDef, formData);
    });
    
    return [...baseFields, ...visibleTypeFields];
  },

  /**
   * Get field definition from schema (baseFields or typeSpecificFields)
   * @param {string} fieldName - Name of the field
   * @param {string} claimType - Type of claim
   * @param {Object} schema - Schema object
   * @returns {Object|null} - Field definition or null if not found
   */
  getFieldDefinition(fieldName, claimType, schema) {
    if (schema.baseFields?.[fieldName]) {
      return schema.baseFields[fieldName];
    }
    
    if (schema.typeSpecificFields?.[claimType]?.[fieldName]) {
      return schema.typeSpecificFields[claimType][fieldName];
    }
    
    return null;
  }
};